// EC-676 - Date Range Field modified

// Import necessary modules and utilities
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import {
    convertDateOnlyStringToDate,
    convertDateToDateOnlyString,
} from "@insite/client-framework/Common/Utilities/DateUtilities";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import updateSearchFields from "@insite/client-framework/Store/Pages/OrderHistory/Handlers/UpdateSearchFields";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SearchFieldWrapper, {
    SearchFieldWrapperStyles,
} from "@insite/content-library/Widgets/OrderHistory/SearchFieldWrapper";
import DatePicker, { DatePickerPresentationProps, DatePickerState } from "@insite/mobius/DatePicker";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import setDateValidation from "@WhiteCap/Handlers/OrderHistoryDateValidation/SetDateValidation";
import CustomApplicationState from "@WhiteCap/Store/Data/CustomApplicationState/CustomApplicationState";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

// Define the widget's own props
interface OwnProps extends WidgetProps {}

// Map state from the Redux store to props
const mapStateToProps = (state: CustomApplicationState) => {
    return {
        parameter: state.pages.orderHistory.getOrdersParameter,
        settings: getSettingsCollection(state),
        fromDateError: state.WhiteCap.orderHistoryDateRangeValidatorStore.fromDateError,
        toDateError: state.WhiteCap.orderHistoryDateRangeValidatorStore.toDateError,
    };
};

// Map dispatch actions to props
const mapDispatchToProps = {
    updateSearchFields,
    setDateValidation,
};

// Combine props from state, dispatch, and widget
type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

// Define styles for the Date Range fields and wrapper
export interface OrderHistoryDateRangeStyles {
    searchFieldWrapper?: SearchFieldWrapperStyles;
    datePickersWrapper?: InjectableCss;
    fromDate?: DatePickerPresentationProps;
    toDate?: DatePickerPresentationProps;
    dateErrorProps?: DatePickerPresentationProps;
}

// Default styles for the widget
export const dateRangeStyles: OrderHistoryDateRangeStyles = {
    datePickersWrapper: {
        css: css`
            display: flex;
        `,
    },
    fromDate: {
        cssOverrides: {
            inputSelect: css`
                width: 100%;
            `,
            formField: css`
                padding-right: 5px;
            `,
        },
        errorProps: {
            css: css`
                max-width: 150px;
            `,
        },
    },
    toDate: {
        cssOverrides: {
            inputSelect: css`
                width: 100%;
            `,
            formField: css`
                padding-left: 5px;
            `,
        },
        errorProps: {
            css: css`
                max-width: 150px;
            `,
        },
    },
};

const styles = dateRangeStyles;


// Main component for the Date Range filter
const OrderHistorySearchFieldDateRange: React.FunctionComponent<Props> = props => {
    const { lookBackDays } = props.settings.orderSettings; // Get look-back days from settings
    const MAX_RANGE_YEARS = 1;

    const fromDateRef = React.useRef<Date | undefined>(
        convertDateOnlyStringToDate(props.parameter.fromDate) || 
        new Date(Date.now() - lookBackDays * 60 * 60 * 24 * 1000)
    );
    const toDateRef = React.useRef<Date | undefined>(
        convertDateOnlyStringToDate(props.parameter.toDate) || 
        new Date()
    );

    const [fromDateState, setFromDateState] = React.useState<Date | undefined>(undefined);
    const [toDateState, setToDateState] = React.useState<Date | undefined>(undefined);

    const setFromDate = React.useCallback((date: Date | undefined) => {
        fromDateRef.current = date;
        setFromDateState(date);
    }, []);

    const setToDate = React.useCallback((date: Date | undefined) => {
        toDateRef.current = date;
        setToDateState(date);
    }, []);


    // Mount effect - set initial dates
    React.useEffect(() => {
        const today = new Date();
        const defaultFromDate = new Date(Date.now() - lookBackDays * 60 * 60 * 24 * 1000);
        const initialFromDate = convertDateOnlyStringToDate(props.parameter.fromDate) || defaultFromDate;
        const initialToDate = convertDateOnlyStringToDate(props.parameter.toDate) || today;

        setFromDate(initialFromDate);
        setToDate(initialToDate);
    }, [props.parameter.fromDate, props.parameter.toDate]);




   const fromDateChangeHandler = ({ selectedDay }: Pick<DatePickerState, "selectedDay">) => {
        if (!selectedDay) return;

        if (toDateRef.current) {
            const maxFromDate = new Date(
                toDateRef.current.getFullYear() - MAX_RANGE_YEARS,
                toDateRef.current.getMonth(),
                toDateRef.current.getDate()
            );
        
            const selectedDayOnly = new Date(selectedDay.getFullYear(), selectedDay.getMonth(), selectedDay.getDate());
            const minDateOnly = new Date(maxFromDate.getFullYear(), maxFromDate.getMonth(), maxFromDate.getDate());


            if (selectedDayOnly < minDateOnly) {
                props.setDateValidation({
                    fromDateError: siteMessage("Order_History_Date_Range_Error", "12") as string,
                    toDateError: "",
                });
                setFromDate(selectedDay);
                props.updateSearchFields({
                    ...props.parameter,
                    fromDate: convertDateToDateOnlyString(selectedDay),
                });
                return;
            }
        }

        props.setDateValidation({ fromDateError: "", toDateError: "" });
        setFromDate(selectedDay);
        props.updateSearchFields({
            ...props.parameter,
            fromDate: convertDateToDateOnlyString(selectedDay),
        });
    };

    const toDateChangeHandler = ({ selectedDay }: Pick<DatePickerState, "selectedDay">) => {
        if (!selectedDay) return;

        if (fromDateRef.current) {
            const maxAllowedToDate = new Date(
                fromDateRef.current.getFullYear() + MAX_RANGE_YEARS,
                fromDateRef.current.getMonth(),
                fromDateRef.current.getDate()
            );
            const selectedDayOnly = new Date(selectedDay.getFullYear(), selectedDay.getMonth(), selectedDay.getDate());
            const maxDateOnly = new Date(maxAllowedToDate.getFullYear(), maxAllowedToDate.getMonth(), maxAllowedToDate.getDate());


            if (selectedDayOnly > maxDateOnly) {
                props.setDateValidation({
                    toDateError: siteMessage("Order_History_Date_Range_Error", "12") as string,
                    fromDateError: "",
                });
                return;
            }
        }

        props.setDateValidation({ fromDateError: "", toDateError: "" });
        setToDate(selectedDay);
        props.updateSearchFields({
            ...props.parameter,
            toDate: convertDateToDateOnlyString(selectedDay),
        });
    };

    const minToDate = fromDateRef.current ? new Date(fromDateRef.current) : undefined;

    // Render the component
    return (
        <SearchFieldWrapper extendedStyles={styles.searchFieldWrapper}>
            <StyledWrapper {...styles.datePickersWrapper} data-test-selector="orderHistory_filterDateRange">
                <DatePicker
                    data-test-selector="orderHistory_filterFromDate"
                    {...styles.fromDate}
                    label={translate("Date Range")}
                    selectedDay={fromDateState}
                    onDayChange={fromDateChangeHandler}
                    dateTimePickerProps={{
                        clearIcon: null,
                        maxDate: new Date(), // Allow navigation up to the current date
                        ...styles.fromDate?.dateTimePickerProps,
                    }}
                    error={props.fromDateError}
                />
                <DatePicker
                    data-test-selector="orderHistory_filterToDate"
                    {...styles.toDate}
                    label={translate("To")}
                    selectedDay={toDateState}
                    onDayChange={toDateChangeHandler}
                    dateTimePickerProps={{
                        clearIcon: null,
                        minDate: minToDate,
                        maxDate: new Date(),
                        ...styles.toDate?.dateTimePickerProps,
                    }}
                    error={props.toDateError}
                />
            </StyledWrapper>
        </SearchFieldWrapper>
    );
};

// Define the widget module configuration
const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(OrderHistorySearchFieldDateRange),
    definition: {
        group: "Order History",
        allowedContexts: ["OrderHistoryPage", "VmiOrderHistoryPage"],
        displayName: "Date Range",
    },
};

export default widgetModule;
// EC-676 - Date Range Field modified
